import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchExpenseComments,
  fetchGroupCommentCounts,
  fetchCommentCountsByExpenseIds,
  addComment,
  updateComment,
  deleteComment,
} from '@/api/comments';
import { Analytics } from '@/services/analytics.service';

export function useGroupCommentCounts(groupId: string | undefined) {
  return useQuery({
    queryKey: ['comment-counts', groupId],
    queryFn: () => fetchGroupCommentCounts(groupId!),
    enabled: !!groupId,
  });
}

export function useCommentCountsByExpenseIds(expenseIds: string[]) {
  return useQuery({
    queryKey: ['comment-counts', 'expenses', expenseIds],
    queryFn: () => fetchCommentCountsByExpenseIds(expenseIds),
    enabled: expenseIds.length > 0,
  });
}

export function useExpenseComments(expenseId: string | undefined) {
  return useQuery({
    queryKey: ['comments', expenseId],
    queryFn: () => fetchExpenseComments(expenseId!),
    enabled: !!expenseId,
  });
}

export function useAddComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      expenseId: string;
      groupId: string;
      userId: string;
      commentText: string;
    }) => addComment(data.expenseId, data.groupId, data.userId, data.commentText),
    onSuccess: (_, { expenseId, groupId, commentText }) => {
      queryClient.invalidateQueries({ queryKey: ['comments', expenseId] });
      queryClient.invalidateQueries({ queryKey: ['comment-counts', groupId] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      Analytics.track({
        name: 'Comment Added',
        properties: { expense_id: expenseId, group_id: groupId, comment_length: commentText.length },
      });
    },
  });
}

export function useUpdateComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { commentId: string; expenseId: string; commentText: string }) =>
      updateComment(data.commentId, data.commentText),
    onSuccess: (_, { expenseId }) => {
      queryClient.invalidateQueries({ queryKey: ['comments', expenseId] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
}

export function useDeleteComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { commentId: string; expenseId: string; groupId?: string }) =>
      deleteComment(data.commentId),
    onSuccess: (_, { commentId, expenseId, groupId }) => {
      queryClient.invalidateQueries({ queryKey: ['comments', expenseId] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      if (groupId) {
        queryClient.invalidateQueries({ queryKey: ['comment-counts', groupId] });
      }
      Analytics.track({
        name: 'Comment Deleted',
        properties: { comment_id: commentId, expense_id: expenseId, group_id: groupId ?? '' },
      });
    },
  });
}
